import { Employee } from './Employee';
import Project from './Project';
import { Timeslot } from './Timeslot';

export class Timesheet {
    uuid?: string;
    createdOn?: string;
    lastModified?: string;
    displayName?: string;
    employee?: Employee;
    timeslots?: Timeslot[];
    expirationDate?: Date;
    status?: TIMESHEET_STATUS;
    totalHours?: string;
    hoursRequired?: number;
    extraInfo?: string;
    happyScore?: number;
    url: string;

    static getTotalMinutes(timesheet: Timesheet): number {
        let totalMinutes = 0;
        timesheet.timeslots.forEach((el) => {
            totalMinutes += el.minutes;
        });
        return totalMinutes;
    }

    static getProjects(timesheet: Timesheet): Project[] {
        const projects: Project[] = [];
        timesheet.timeslots.forEach((el) => {
            projects.push(el.project);
        });
        return projects;
    }

    static getTimeslots(timesheet: Timesheet): Timeslot[] {
        const timeslots: Timeslot[] = [];
        timesheet.timeslots.forEach((el) => {
            timeslots.push(el);
        });
        return timeslots;
    }

    static getTotalDaysFromTimesheet(timesheet: Timesheet): number {
        const { hoursPerDay } = Timesheet.getEffectiveHoursPerDay(timesheet);
        return Math.round((Timesheet.getTotalMinutes(timesheet) / 60 / hoursPerDay) * 10) / 10;
    }

    static getTotalDaysFromMinutes(minutes: number, hoursPerDay: number = 8): number {
        return Math.round((minutes / 60 / hoursPerDay) * 10) / 10;
    }

    /**
     * Determines the workday length that should apply for this timesheet's required-hours math.
     * SD Worx exports always assume an 8h/day baseline, but a project can define a shorter
     * contractual workday via `Project.hoursPerDay` (e.g. 7.5h). When every timeslot on this
     * timesheet points at (a) project(s) sharing that same non-default rate, we override the
     * SD Worx baseline so "Required hours" reflects the actual agreement instead of a false shortfall.
     */
    static getEffectiveHoursPerDay(timesheet: Timesheet): { hoursPerDay: number; overridden: boolean; projectName?: string } {
        const rates = new Set<number>();
        let projectName: string;
        (timesheet.timeslots || []).forEach((slot) => {
            const rate = slot.project?.hoursPerDay;
            if (rate && rate > 0 && rate !== 8) {
                rates.add(rate);
                projectName = slot.project.title;
            }
        });

        if (rates.size === 1) {
            return { hoursPerDay: [...rates][0], overridden: true, projectName };
        }
        return { hoursPerDay: 8, overridden: false };
    }

    /**
     * `hoursRequired` as stored (manual edit, SD Worx import, Jira default) always assumes an
     * 8h/day baseline. Rescale it to the project's actual workday length when applicable.
     */
    static getEffectiveRequiredHours(timesheet: Timesheet): number {
        const { hoursPerDay, overridden } = Timesheet.getEffectiveHoursPerDay(timesheet);
        if (!overridden || !timesheet.hoursRequired) {
            return timesheet.hoursRequired;
        }
        return Math.round(((timesheet.hoursRequired * hoursPerDay) / 8) * 100) / 100;
    }

    static getRequiredHoursDiff(timesheet: Timesheet) {
        return Math.round(Math.abs(Timesheet.getTotalMinutes(timesheet) / 60 - Timesheet.getEffectiveRequiredHours(timesheet)) * 60);
    }

    static getRequiredHoursDiffPercentage(timesheet: Timesheet) {
        return Math.round((Timesheet.getTotalMinutes(timesheet) / 60 / Timesheet.getEffectiveRequiredHours(timesheet)) * 100);
    }

    static getActualIncome(timesheet: Timesheet, projects: Project[]) {
        let total = 0;
        timesheet.timeslots.forEach((slot) => {
            if (slot.project.billable) {
                let project = projects.find((w) => w.uuid == slot.project.uuid);
                if (project && project.linkedEmployees) {
                    let link = project.linkedEmployees.find((link) => link.employee.uuid == timesheet.employee.uuid);
                    if (link) {
                        let price = link.pricePerHour;
                        total += (slot.minutes / 60) * price;
                    }
                }
            }
        });
        return total;
    }
}

export class TIMESHEET_STATUS {
    static readonly EDIT = new TIMESHEET_STATUS('EDIT', 'tag', 'EDIT');
    static readonly REVIEW = new TIMESHEET_STATUS('REVIEW', 'tag tag--danger', 'REVIEW');
    static readonly DONE = new TIMESHEET_STATUS('DONE', 'tag tag--green', 'APPROVED');

    private constructor(public readonly key: string, public readonly cssClass: string, public readonly displayName: string) {}

    static getStatus(value: string): TIMESHEET_STATUS {
        switch (value) {
            case this.EDIT.key:
                return this.EDIT;
            case this.REVIEW.key:
                return this.REVIEW;
            case this.DONE.key:
                return this.DONE;
        }
        return null;
    }
}
